import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, Egg, Bird } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import {
  enablePush,
  disablePush,
  getPushSupport,
  getNotificationPermission,
  getExistingSubscription,
} from "@/lib/pushManager";

const POLL_MS = 15000;
const LS_LAST_SEEN_PREFIX = "aviary_last_seen_activity_";

function relTime(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (lang === "ar") {
    if (diffSec < 60) return "الآن";
    const m = Math.floor(diffSec / 60);
    if (m < 60) return `${m}د`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}س`;
    return `${Math.floor(h / 24)}ي`;
  }
  if (diffSec < 60) return "just now";
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const { activeGroup } = useGroups();
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushOn, setPushOn] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const sinceRef = useRef(null);
  const dropdownOpenRef = useRef(false);
  const groupRef = useRef(null);

  const lastSeenKey = activeGroup?.id ? `${LS_LAST_SEEN_PREFIX}${activeGroup.id}` : null;

  // Detect push support & state
  useEffect(() => {
    const support = getPushSupport();
    setPushSupported(!!support.supported);
    if (!support.supported) return;
    (async () => {
      const perm = await getNotificationPermission();
      const sub = await getExistingSubscription();
      setPushOn(perm === "granted" && !!sub);
    })();
  }, []);

  // Reset when group changes
  useEffect(() => {
    if (!activeGroup?.id) return;
    groupRef.current = activeGroup.id;
    sinceRef.current = null;
    setItems([]);
    setUnread(0);
  }, [activeGroup?.id]);

  // Poll activities
  useEffect(() => {
    if (!activeGroup?.id || !user?.id) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const params = sinceRef.current ? { since: sinceRef.current } : {};
        const { data } = await api.get("/activities", { params });
        if (cancelled) return;
        const fresh = (data.activities || []).filter((a) => a.group_id === groupRef.current);
        if (fresh.length) {
          sinceRef.current = fresh[0].created_at;
        }
        setItems((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...fresh.filter((a) => !seen.has(a.id)), ...prev];
          return merged.slice(0, 30);
        });

        // Toast + unread for activities by OTHER users
        const lastSeen = lastSeenKey ? localStorage.getItem(lastSeenKey) : null;
        const ownerOthers = fresh.filter((a) => a.user_id !== user.id);
        if (ownerOthers.length && !dropdownOpenRef.current) {
          // show a single combined toast for fresh ones since last poll cycle
          const newSince = lastSeen ? ownerOthers.filter((a) => a.created_at > lastSeen) : ownerOthers;
          if (newSince.length === 1) {
            const a = newSince[0];
            toast(`${a.user_name} ${t("notif.updated")} ${a.target}`, { duration: 5000 });
          } else if (newSince.length > 1) {
            toast(`${newSince.length} ${t("notif.new_updates")}`, { duration: 5000 });
          }
        }
        if (ownerOthers.length) {
          setUnread((u) => {
            if (dropdownOpenRef.current) return 0;
            return u + ownerOthers.filter((a) => !lastSeen || a.created_at > lastSeen).length;
          });
        }
      } catch {}
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup?.id, user?.id, lang]);

  const handleOpenChange = (open) => {
    dropdownOpenRef.current = open;
    if (open && items.length && lastSeenKey) {
      // Mark all current as seen
      try { localStorage.setItem(lastSeenKey, items[0].created_at); } catch {}
      setUnread(0);
    }
  };

  const togglePush = async () => {
    if (pushOn) {
      const r = await disablePush();
      if (r.ok) {
        setPushOn(false);
        toast.success(t("notif.push_disabled"));
      }
    } else {
      const r = await enablePush();
      if (r.ok) {
        setPushOn(true);
        toast.success(t("notif.push_enabled"));
      } else if (r.reason === "denied") {
        toast.error(t("notif.push_denied"));
      } else {
        toast.error(t("notif.push_failed"));
      }
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative bg-cream-2 hover:bg-app-border p-1.5 rounded-lg transition-colors"
          data-testid="notifications-bell"
          aria-label={t("notif.title")}
        >
          <Bell className="w-3.5 h-3.5 text-espresso/70" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-danger text-white text-[10px] font-bold rounded-full grid place-items-center"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-cream-2 border-b border-app-border">
          <DropdownMenuLabel className="p-0 text-[10px] uppercase tracking-widest text-olive">
            {t("notif.title")}
          </DropdownMenuLabel>
          {pushSupported && (
            <button
              onClick={togglePush}
              className={`flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-md transition-colors ${
                pushOn ? "bg-olive text-white" : "bg-white text-espresso/70 hover:bg-app-border"
              }`}
              data-testid="notifications-push-toggle"
            >
              {pushOn ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
              {pushOn ? t("notif.push_on") : t("notif.push_off")}
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-espresso/50">
              {t("notif.empty")}
            </div>
          ) : (
            items.map((a) => {
              const isOwn = a.user_id === user?.id;
              const Icon = a.kind === "couple_update" ? Egg : Bird;
              return (
                <div
                  key={a.id}
                  className={`flex items-start gap-2 px-3 py-2 border-b border-app-border/60 ${
                    isOwn ? "opacity-60" : ""
                  }`}
                  data-testid={`notification-item-${a.id}`}
                >
                  <Icon className="w-3.5 h-3.5 mt-0.5 text-olive flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-espresso">
                      <span className="font-semibold">{a.user_name}</span>{" "}
                      <span className="text-espresso/70">{t("notif.updated")}</span>{" "}
                      <span className="font-semibold">{a.target}</span>
                    </div>
                    <div className="text-[10px] text-espresso/50 mt-0.5">{relTime(a.created_at, lang)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {!pushSupported && (
          <>
            <DropdownMenuSeparator />
            <div className="px-3 py-2 text-[10px] text-espresso/50 leading-relaxed">
              {t("notif.unsupported")}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
